import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMolecule } from '@peruselab/api-client/molecules';
import { getIsomer } from '@peruselab/api-client/isomers';
import { createIllustration } from '@peruselab/api-client/illustrations';
import {
  buildIsomerIllustrationElement,
  buildMoleculeIllustrationElement,
  buildReactionIllustrationElement,
  type IllustrationElement,
  type IllustrationElementPick,
  type IllustrationPosition
} from '@peruselab/structure-editor/domain/illustrationElement';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';

export type ArmedIllustrationPick =
  | { readonly kind: 'isomer'; readonly isomerId: string; readonly moleculeId: string; readonly isomerRevision: number }
  | { readonly kind: 'molecule'; readonly moleculeId: string; readonly moleculeRevision: number }
  | { readonly kind: 'reaction'; readonly reactionId: string; readonly reactionRevision: number };

type SaveState =
  | { readonly state: 'idle' }
  | { readonly state: 'saving' }
  | { readonly state: 'success'; readonly message: string }
  | { readonly state: 'error'; readonly message: string };

let nextLocalId = 0;
function createLocalId(): string {
  nextLocalId += 1;
  return `illustration-element-${nextLocalId}`;
}

export function useIllustrationComposer(paperSize: PaperSize) {
  const { t } = useTranslation();
  const [elements, setElements] = useState<readonly IllustrationElementPick[]>([]);
  const [armedPick, setArmedPick] = useState<ArmedIllustrationPick | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ state: 'idle' });

  const armIsomer = useCallback((isomerId: string, moleculeId: string, isomerRevision: number) => {
    setArmedPick({ kind: 'isomer', isomerId, moleculeId, isomerRevision });
  }, []);

  const armMolecule = useCallback(async (moleculeId: string) => {
    const molecule = await getMolecule(moleculeId);
    setArmedPick({ kind: 'molecule', moleculeId, moleculeRevision: molecule.documentRevision });
  }, []);

  const armReaction = useCallback((reactionId: string, reactionRevision: number) => {
    setArmedPick({ kind: 'reaction', reactionId, reactionRevision });
  }, []);

  const cancelArmed = useCallback(() => setArmedPick(null), []);

  const placeArmedAt = useCallback(
    (position: IllustrationPosition) => {
      if (!armedPick) return;
      const localId = createLocalId();
      const placed: IllustrationElementPick =
        armedPick.kind === 'isomer'
          ? {
              kind: 'isomer',
              localId,
              isomerId: armedPick.isomerId,
              moleculeId: armedPick.moleculeId,
              isomerRevision: armedPick.isomerRevision,
              position
            }
          : armedPick.kind === 'molecule'
            ? {
                kind: 'molecule',
                localId,
                moleculeId: armedPick.moleculeId,
                moleculeRevision: armedPick.moleculeRevision,
                position
              }
            : {
                kind: 'reaction',
                localId,
                reactionId: armedPick.reactionId,
                reactionRevision: armedPick.reactionRevision,
                position
              };
      setElements((current) => [...current, placed]);
      setArmedPick(null);
    },
    [armedPick]
  );

  const moveElement = useCallback((localId: string, position: IllustrationPosition) => {
    setElements((current) =>
      current.map((element) => (element.localId === localId ? { ...element, position } : element))
    );
  }, []);

  const removeElement = useCallback((localId: string) => {
    setElements((current) => current.filter((element) => element.localId !== localId));
  }, []);

  const updateLabel = useCallback((localId: string, label: string) => {
    const trimmed = label.trim();
    setElements((current) =>
      current.map((element) => {
        if (element.localId !== localId) return element;
        const { label: _existingLabel, ...withoutLabel } = element;
        if (!trimmed) return withoutLabel as IllustrationElementPick;
        return { ...withoutLabel, label: trimmed } as IllustrationElementPick;
      })
    );
  }, []);

  const canSave = elements.length > 0 && saveState.state !== 'saving';

  const save = useCallback(
    async (title?: string) => {
      if (elements.length === 0) {
        setSaveState({ state: 'error', message: 'An illustration needs at least one element.' });
        return;
      }
      setSaveState({ state: 'saving' });
      try {
        const built: IllustrationElement[] = await Promise.all(
          elements.map(async (pick) => {
            if (pick.kind === 'isomer') {
              const [molecule, isomer] = await Promise.all([getMolecule(pick.moleculeId), getIsomer(pick.isomerId)]);
              return buildIsomerIllustrationElement(
                molecule.document,
                isomer.document,
                pick.isomerId,
                pick.isomerRevision,
                pick.position,
                pick.label,
                paperSize
              );
            }
            if (pick.kind === 'molecule') {
              return buildMoleculeIllustrationElement(
                pick.moleculeId,
                pick.moleculeRevision,
                pick.position,
                pick.label
              );
            }
            return buildReactionIllustrationElement(pick.reactionId, pick.reactionRevision, pick.position, pick.label);
          })
        );
        const created = await createIllustration(
          {
            schemaVersion: 'chem-drawing.v11',
            kind: 'illustration',
            elements: built
          },
          title
        );
        setSaveState({
          state: 'success',
          message: t('illustrationComposer.created', {
            title: created.title || title || t('illustrationComposer.untitled')
          })
        });
      } catch (error) {
        setSaveState({
          state: 'error',
          message: error instanceof Error ? error.message : 'Unable to save this illustration.'
        });
      }
    },
    [elements, paperSize, t]
  );

  return {
    elements,
    armedPick,
    armIsomer,
    armMolecule,
    armReaction,
    cancelArmed,
    placeArmedAt,
    moveElement,
    removeElement,
    updateLabel,
    saveState,
    canSave,
    save
  };
}
